import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { clampOffsetMinutes } from '@vendorops/shared';
import {
  PX_PER_MINUTE,
  SNAP_MINUTES,
  edgeDrag,
  offsetForTop,
  offsetFromDelta,
  snapMinutes,
  topForOffset,
  type DragEdge,
  type TimelineScale,
} from '@/lib/timeline-geometry';

/**
 * The timeline's pointer gestures: a block under a pointer, and a span being
 * swept out of the coverage rail beside it.
 *
 * Two hooks, one discipline. Both are *differential*: a drag is `clientY` now
 * minus `clientY` at the grab, plus whatever the scroller moved underneath in
 * between, and a sweep is `clientY` now against the offset the press landed on
 * — subtraction either way, no measurement. That is what lets both be driven in
 * a DOM with no layout engine, and it is also what makes them correct in a real
 * browser, where a box read halfway through a gesture is a box that may have
 * moved for reasons the gesture knows nothing about.
 *
 * One quantity genuinely cannot be reached by subtraction: where the scroller's
 * window sits, which auto-scroll needs to know when the pointer is near an edge
 * of it. That arrives through `viewport`, injected by the axis, read *once* at
 * `pointerdown` and held for the gesture.
 *
 * Every minute this produces comes out of `timeline-geometry`. Nothing here
 * divides a pixel by two.
 */

/**
 * Movement below this is a click, not a drag.
 *
 * Four pixels, because a pointer that never moved on purpose still moves: a
 * trackpad tap drifts a pixel or two and a finger on glass drifts more. Without
 * a floor, every click on a block also files a one-minute retime — and the
 * click that follows the drag opens the editor over the top of it, which is the
 * bug this interaction always has.
 */
export const DRAG_THRESHOLD_PX = 4;

/** How close to the edge of the scroller a held pointer starts pulling the day. */
export const AUTO_SCROLL_EDGE_PX = 48;

/** And how fast — about 480px a second, which crosses a 12½-hour day in three. */
export const AUTO_SCROLL_STEP_PX = 8;

/** Body retimes; an edge re-lengths. Three targets, because they mean three things. */
export type DragKind = 'body' | DragEdge;

/**
 * Which way a held pointer near the edge of the scroller pulls the day:
 * -1 up, 0 still, 1 down.
 *
 * Reads the one measurement a gesture takes, so a scroller of no measured
 * height — which is every scroller in a DOM with no layout engine — simply
 * does not scroll rather than scrolling always.
 */
function edgePull(
  clientY: number,
  window: ScrollWindow,
  scrollTop: number,
  scroller: HTMLElement | null,
): number {
  if (!scroller || window.height <= 0) return 0;
  const pointerCanvasY = clientY - window.top + scrollTop;
  if (pointerCanvasY < scrollTop + AUTO_SCROLL_EDGE_PX) return -1;
  if (pointerCanvasY > scrollTop + window.height - AUTO_SCROLL_EDGE_PX) return 1;
  return 0;
}

/**
 * One frame of that pull. False when the day has nowhere further to go.
 *
 * The answer matters rather than the side effect: against the end of the day
 * the caller has to *stop*, not spin a frame loop that can no longer change
 * anything.
 */
function stepScroll(
  scroller: HTMLElement,
  direction: number,
  totalHeight: number,
  windowHeight: number,
): boolean {
  const before = scroller.scrollTop;
  const limit = Math.max(0, totalHeight - windowHeight);
  const next = Math.min(limit, Math.max(0, before + direction * AUTO_SCROLL_STEP_PX));
  if (next === before) return false;
  scroller.scrollTop = next;
  return true;
}

/**
 * Take the pointer capture, which is what lets a gesture survive the pointer
 * leaving the handle it started on.
 *
 * Module-level because both gestures in this file need it and neither owns it.
 * A browser that refuses the capture still delivers moves to the element under
 * the pointer, so this is an optimisation rather than the mechanism — hence the
 * swallowed throw.
 */
function takeCapture(g: { target: HTMLElement; pointerId: number; captured: boolean }) {
  if (g.captured) return;
  g.captured = true;
  try {
    g.target.setPointerCapture(g.pointerId);
  } catch {
    // See above: capture is not the mechanism.
  }
}

/**
 * The rAF loop that pulls the day past a held pointer.
 *
 * One loop for both gestures in this file, because "scroll the day while the
 * pointer is held near an edge" is one behaviour and two copies of a frame loop
 * are two places for a cancelled animation frame to be forgotten — which leaks
 * as a day that keeps scrolling after the pointer is long gone. `drive` runs
 * once per frame and answers whether there is another frame's worth of day
 * left; the loop owns nothing else.
 */
function useAutoScroll(drive: () => boolean) {
  const frame = useRef<number | null>(null);
  // Re-read every render rather than closed over, for the same reason `host`
  // is: a frame landing between renders must drive the scale the axis is
  // actually drawing.
  const driveRef = useRef(drive);
  driveRef.current = drive;

  const stop = useCallback(() => {
    if (frame.current === null) return;
    cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const tick = useCallback(() => {
    frame.current = null;
    if (driveRef.current()) frame.current = requestAnimationFrame(tick);
  }, []);

  /** Start the loop, or stop it — whichever the gesture now wants. */
  const pump = useCallback(
    (active: boolean) => {
      if (!active) {
        stop();
        return;
      }
      if (frame.current === null) frame.current = requestAnimationFrame(tick);
    },
    [stop, tick],
  );

  return { pump, stop };
}

/** What a drag needs to know about the block it has hold of. */
export interface DragSubject {
  id: string;
  offsetMinutes: number;
  /**
   * The stored field, where **null is not zero**: it is what makes the trailing
   * block follow the wedding's end time, and it is the difference between a
   * drag that preserves that and one that destroys it.
   */
  durationMinutes: number | null;
  /** The length the block is *drawn* with — the edge the planner grabbed. */
  effectiveDurationMinutes: number;
}

/** One PATCH's worth of change. Absent means "leave it alone". */
export interface TimelineRetime {
  offsetMinutes?: number;
  durationMinutes?: number | null;
}

/** The live position of the block under the pointer, in minutes. */
export interface TimelineDragState {
  id: string;
  kind: DragKind;
  offsetMinutes: number;
  /** What the block is drawn as right now — always a number while dragging. */
  durationMinutes: number;
  fromOffsetMinutes: number;
  fromDurationMinutes: number;
}

export interface ScrollWindow {
  /** The scroller's top edge, in client coordinates. */
  top: number;
  /** How much of the day it can show at once. Zero means it is not scrolling. */
  height: number;
}

export interface TimelineDragHost {
  /** The scale the axis is rendering *now* — expansions included. */
  scale: TimelineScale;
  totalHeight: number;
  /** Where the scroller's window is. Read once per gesture, never mid-move. */
  viewport: (scroller: HTMLElement) => ScrollWindow;
  /** One PATCH, on drop. */
  commit: (id: string, patch: TimelineRetime) => void;
  /**
   * The live offset has moved, and the view may need to follow it onto another
   * calendar day.
   *
   * Answers with the pixels the canvas shifted as a result, which the gesture
   * subtracts back out of its own frame — because `dy` below carries
   * `(scrollTop - grabScrollTop)`, a scroller moving under a held pointer being
   * as much a drag as a pointer moving over a still scroller. A view that moved
   * for its own reasons is neither, and uncorrected one day flip would add
   * 2880px — a whole calendar day — to the time the drag computes.
   *
   * Zero when nothing moved, which is every gesture on a wedding whose day
   * never leaves the canvas. Optional because two of the four instances of this
   * hook drive a draft nobody has named yet, and paging the day away from a
   * half-typed label is not a thing to do to a planner.
   */
  follow?: (offsetMinutes: number) => number;
}

interface Gesture {
  subject: DragSubject;
  kind: DragKind;
  pointerId: number;
  scroller: HTMLElement | null;
  window: ScrollWindow;
  /** Where the pointer went down, and where the scroller was then. */
  grabClientY: number;
  grabScrollTop: number;
  clientY: number;
  fine: boolean;
  /** Past the threshold — this is a drag now, and no longer a click. */
  moved: boolean;
  /** What would take the capture, and whether it has taken it yet. */
  target: HTMLElement;
  captured: boolean;
  scale: TimelineScale;
  totalHeight: number;
  /** -1 up, 0 still, 1 down. A plain number: `apply` rewrites it mid-frame. */
  autoScroll: number;
  state: TimelineDragState;
}

/** The offset whose pixel the gesture has hold of. */
function anchorOffset(g: Gesture): number {
  return g.kind === 'bottom'
    ? g.subject.offsetMinutes + g.subject.effectiveDurationMinutes
    : g.subject.offsetMinutes;
}

export function useTimelineDrag(host: TimelineDragHost) {
  // Re-read every render rather than closed over: a pointermove landing between
  // renders must see the scale the axis is actually drawing, not the one it was
  // drawing when the gesture started.
  const hostRef = useRef(host);
  hostRef.current = host;

  const [drag, setDrag] = useState<TimelineDragState | null>(null);
  const gesture = useRef<Gesture | null>(null);
  /** A drag just ended, so the click the browser sends next is not a click. */
  const swallowClick = useRef(false);

  /**
   * Where the drag stands, from the two numbers that define it.
   *
   * Called from pointermove *and* from the auto-scroll frame, because a
   * scroller moving under a held-still pointer is as much a drag as a pointer
   * moving over a still scroller.
   */
  const apply = useCallback((g: Gesture) => {
    const scrollTop = g.scroller ? g.scroller.scrollTop : 0;
    if (!g.moved && Math.abs(g.clientY - g.grabClientY) < DRAG_THRESHOLD_PX) return;
    g.moved = true;
    /* A body's capture waits until here, which is what this line is.
       Capture retargets the `click` that follows to the capturing element, so
       taking it on pointerdown delivered a plain click on a field to the block
       behind it and left every field on the axis dead to the mouse. Nothing
       caught it: jsdom implements `setPointerCapture` as a no-op and dispatches
       the click to the button regardless, so thirty-one passing tests said the
       fields worked. Below the threshold there is no drag to keep alive, so
       waiting costs the body nothing — see `onPointerDown` for why it costs an
       edge the whole gesture. */
    takeCapture(g);

    const dy = g.clientY - g.grabClientY + (scrollTop - g.grabScrollTop);
    const anchor = anchorOffset(g);

    const anchorTop = topForOffset(g.scale, anchor);

    // The pixel the pointer is on, read back as a time through the *same*
    // piecewise map the axis drew with — then handed to the geometry as the
    // distance it would have been on an axis with nothing collapsed in it.
    const reached = offsetForTop(g.scale, anchorTop + dy);
    const linearDy = (reached - anchor) * PX_PER_MINUTE;
    const opts = { fine: g.fine };

    let offsetMinutes: number;
    let durationMinutes: number;
    if (g.kind === 'body') {
      offsetMinutes = clampOffsetMinutes(offsetFromDelta(g.subject.offsetMinutes, linearDy, opts));
      durationMinutes = g.subject.effectiveDurationMinutes;
    } else {
      const moved = edgeDrag(
        {
          offsetMinutes: g.subject.offsetMinutes,
          durationMinutes: g.subject.effectiveDurationMinutes,
        },
        linearDy,
        g.kind,
        opts,
      );
      offsetMinutes = clampOffsetMinutes(moved.offsetMinutes);
      durationMinutes = moved.durationMinutes + (moved.offsetMinutes - offsetMinutes);
    }

    if (offsetMinutes !== g.state.offsetMinutes || durationMinutes !== g.state.durationMinutes) {
      g.state = { ...g.state, offsetMinutes, durationMinutes };
      setDrag(g.state);
    }

    // The view follows the block onto the day it has reached, and the gesture
    // un-does whatever that did to the scroller. The offset it is handed is the
    // one the drag is reporting — a block's start, which is what a body drag
    // and a top-edge drag both move.
    //
    // A bottom-edge drag is skipped rather than handed that start. It leaves
    // the start where it is, so there is no day for the view to follow — but
    // the start is the only offset this has, and passing it pages to the
    // *start's* day, which is not where the gesture is. A block running from
    // 11 PM to 1:30 AM has its bottom edge on the day after midnight, and that
    // is the only place a planner can take hold of it; handing the unmoved
    // start to `follow` yanked the view back to the wedding day on the first
    // pointermove, leaving them resizing something they could no longer see.
    // Following the *end* over midnight is not the answer either — that would
    // carry the block's own top off the canvas. A bottom-edge drag simply has
    // no day to follow.
    if (g.kind !== 'bottom') {
      g.grabScrollTop += hostRef.current.follow?.(offsetMinutes) ?? 0;
    }

    // Near an edge of the scroller and still held: pull the day past the
    // pointer.
    g.autoScroll = edgePull(g.clientY, g.window, scrollTop, g.scroller);
  }, []);

  const { pump, stop: stopScrolling } = useAutoScroll(() => {
    const g = gesture.current;
    if (!g || !g.scroller || g.autoScroll === 0) return false;
    if (!stepScroll(g.scroller, g.autoScroll, g.totalHeight, g.window.height)) {
      g.autoScroll = 0;
      return false;
    }
    apply(g);
    return g.autoScroll !== 0;
  });

  const finish = useCallback(
    (commit: boolean) => {
      const g = gesture.current;
      gesture.current = null;
      stopScrolling();
      setDrag(null);
      if (!g || !g.moved) return;

      // The click the browser sends after a real drag is the drag's own
      // shadow, not a request to open anything.
      swallowClick.current = true;
      if (!commit) return;

      const patch: TimelineRetime = {};
      if (g.kind !== 'bottom' && g.state.offsetMinutes !== g.subject.offsetMinutes) {
        patch.offsetMinutes = g.state.offsetMinutes;
      }
      // A block that stores null already means "the end stays where it is", so
      // a top-edge drag holds the end by moving the start alone. Sending a
      // number to hold an end that null was holding anyway is how the trailing
      // block silently stops following the wedding's end time.
      const keepsOpenEnd = g.kind === 'top' && g.subject.durationMinutes === null;
      if (
        g.kind !== 'body' &&
        !keepsOpenEnd &&
        g.state.durationMinutes !== g.subject.durationMinutes
      ) {
        patch.durationMinutes = g.state.durationMinutes;
      }
      // An empty PATCH is not a request, and the API rejects it by design.
      if (Object.keys(patch).length > 0) hostRef.current.commit(g.subject.id, patch);
    },
    [stopScrolling],
  );

  const onPointerDown = useCallback(
    (subject: DragSubject, kind: DragKind, event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0 || gesture.current) return;
      swallowClick.current = false;

      const target = event.currentTarget;
      const scroller = target.closest('[data-timeline-scroll]') as HTMLElement | null;
      const { scale, totalHeight, viewport } = hostRef.current;

      gesture.current = {
        subject,
        kind,
        pointerId: event.pointerId ?? 0,
        scroller,
        window: scroller ? viewport(scroller) : { top: 0, height: 0 },
        grabClientY: event.clientY,
        grabScrollTop: scroller ? scroller.scrollTop : 0,
        clientY: event.clientY,
        fine: event.altKey,
        moved: false,
        target,
        captured: false,
        scale,
        totalHeight,
        autoScroll: 0,
        state: {
          id: subject.id,
          kind,
          offsetMinutes: subject.offsetMinutes,
          durationMinutes: subject.effectiveDurationMinutes,
          fromOffsetMinutes: subject.offsetMinutes,
          fromDurationMinutes: subject.effectiveDurationMinutes,
        },
      };

      /**
       * An edge takes the capture now; a body waits for the threshold.
       *
       * Deferring is right for the body, which is also a click target — see
       * `apply` for what taking it early did to the fields. An edge is not:
       * nothing listens for a click on an eight-pixel resize strip, so there
       * is no click to protect and nothing to wait for. A sweep reaches the
       * same conclusion from the same premise.
       *
       * And waiting costs an edge the gesture outright. The threshold is
       * measured inside `apply`, which only runs from the handlers on the
       * element the pointer went down on — so an edge drag had to keep the
       * pointer inside eight pixels until the first move was *seen*, and one
       * ordinary mouse move is further than that. Which sibling happened to be
       * under the pointer then decided whether the drag lived: off a draft's
       * bottom edge going down is empty day, which listens for nothing, and
       * the gesture was lost; going up it is the draft's own body, which
       * carries the same handlers, and the gesture survived. A resize that
       * could only ever shorten.
       */
      if (kind !== 'body') takeCapture(gesture.current);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g) return;
      g.clientY = event.clientY;
      g.fine = event.altKey;
      apply(g);
      pump(g.autoScroll !== 0);
    },
    [apply, pump],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g) return;
      g.clientY = event.clientY;
      g.fine = event.altKey;
      apply(g);
      finish(true);
    },
    [apply, finish],
  );

  const onPointerCancel = useCallback(() => finish(false), [finish]);

  /** Did a drag just end here? Then the click behind it is not one. */
  const claimClick = useCallback(() => {
    if (!swallowClick.current) return false;
    swallowClick.current = false;
    return true;
  }, []);

  return { drag, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, claimClick };
}

/** A span the planner swept out, in the grammar the rows are stored in. */
export interface CoverageSpan {
  offsetMinutes: number;
  durationMinutes: number;
}

export interface CoverageSweepHost {
  /** The scale the axis is rendering *now*. */
  scale: TimelineScale;
  totalHeight: number;
  /** Where the scroller's window is. Read once per gesture, never mid-move. */
  viewport: (scroller: HTMLElement) => ScrollWindow;
  /**
   * The shortest *swept* span that is a window at all. Below this the pixels
   * the planner drew are a tap or a twitch rather than a length, so the span is
   * thrown away and `defaultMinutes` is used instead. Passed in rather than
   * named here because the number belongs to the coverage schema, not to a
   * pointer gesture.
   */
  minMinutes: number;
  /**
   * The length to use when the swept span came out under `minMinutes` — which
   * a plain click always does, having swept nothing at all.
   *
   * The distinction is the whole of what changed here. `minMinutes` used to
   * decide whether there was a window at all: under it, the gesture cancelled
   * and a press on the rail did nothing. That made a click a dead end on the
   * one surface a planner could not tell was a surface, and the opposite of the
   * day beside it, where a click on empty space creates an item. So the floor
   * still governs the *span* — a two-pixel drag is not a two-minute booking —
   * but a press on the rail now always means "make something here", because
   * that is plainly what it was for.
   */
  defaultMinutes: number;
  /** One span, on release. Its length is the swept one, or `defaultMinutes`. */
  onSweep: (span: CoverageSpan) => void;
}

interface SweepGesture {
  pointerId: number;
  scroller: HTMLElement | null;
  window: ScrollWindow;
  target: HTMLElement;
  captured: boolean;
  scale: TimelineScale;
  totalHeight: number;
  /** The offset the pointer went down on, fixed for the gesture. */
  from: number;
  clientY: number;
  /** -1 up, 0 still, 1 down. A plain number: the frame loop rewrites it. */
  autoScroll: number;
  span: CoverageSpan;
}

/**
 * A span being swept out of empty rail — the gesture that *creates* a coverage
 * window, where `useTimelineDrag` moves one that already exists.
 *
 * A click is the degenerate case of it rather than a second gesture: a press
 * and a release at one pixel sweep a span of no length, and `finish` reads that
 * as the planner asking for a window here of the default length. See
 * `defaultMinutes`, which is where the two readings are told apart.
 *
 * In this file rather than in the axis, and the deciding fact is auto-scroll. A
 * sweep is a *long* gesture: a photographer's nine hours is 1080px at this
 * scale against a scroller capped at 70vh, so the ordinary window a planner
 * wants to draw does not fit on screen, and a sweep that cannot pull the day
 * past the pointer cannot draw it at all. Everything that pull needs — the
 * scroller's one measured window, the frame loop, the edge test, the capture
 * that survives the pointer leaving a twelve-pixel strip — is here already and
 * is shared outright above. The axis's own `startDraft` is the sibling this
 * gesture extends, and it stays there because a click needs none of that.
 *
 * What is *not* shared is the lifecycle, which is why this is a second hook
 * rather than a fourth `DragKind`. A drag has a subject with an id and commits
 * a PATCH the moment the pointer lifts. A sweep has no subject, and the pointer
 * lifting is the middle of the interaction rather than the end of it: what it
 * produces is two numbers that then wait for a planner to name a tag before
 * anything is posted at all. Folding that into `Gesture` would put an optional
 * id and an optional subject on every block drag to describe the one gesture
 * that has neither.
 *
 * Differential in the same way, and for the same reason: `clientY` minus the
 * scroller's own `scrollTop`, through the same `offsetForTop` the axis drew
 * with. Nothing here measures a box mid-gesture.
 */
export function useCoverageSweep(host: CoverageSweepHost) {
  const hostRef = useRef(host);
  hostRef.current = host;

  /** The span under the pointer right now, for the provisional capsule. */
  const [sweep, setSweep] = useState<CoverageSpan | null>(null);
  const gesture = useRef<SweepGesture | null>(null);

  /**
   * Which offset a client Y lands on.
   *
   * Clamped as well as snapped, and the clamp is not cosmetic: the axis can be
   * scrolled past the furthest offset the schema stores, so an unclamped sweep
   * at the very top of a multi-day axis would post an offset the route refuses.
   * It is the same clamp `startDraft` applies to a click, for the same reason.
   */
  const offsetAt = (g: SweepGesture, clientY: number) => {
    const scrollTop = g.scroller ? g.scroller.scrollTop : 0;
    return clampOffsetMinutes(
      snapMinutes(offsetForTop(g.scale, clientY - g.window.top + scrollTop)),
    );
  };

  const apply = useCallback((g: SweepGesture) => {
    const to = offsetAt(g, g.clientY);
    // Either direction. A planner sweeping upward from eight o'clock to eleven
    // has named the same window as one sweeping down, and the earlier of the
    // two edges is the offset whatever order they were touched in.
    const span = {
      offsetMinutes: Math.min(g.from, to),
      durationMinutes: Math.abs(to - g.from),
    };
    if (
      span.offsetMinutes !== g.span.offsetMinutes ||
      span.durationMinutes !== g.span.durationMinutes
    ) {
      g.span = span;
      setSweep(span);
    }
    g.autoScroll = edgePull(
      g.clientY,
      g.window,
      g.scroller ? g.scroller.scrollTop : 0,
      g.scroller,
    );
  }, []);

  const { pump, stop: stopScrolling } = useAutoScroll(() => {
    const g = gesture.current;
    if (!g || !g.scroller || g.autoScroll === 0) return false;
    if (!stepScroll(g.scroller, g.autoScroll, g.totalHeight, g.window.height)) {
      g.autoScroll = 0;
      return false;
    }
    apply(g);
    return g.autoScroll !== 0;
  });

  const finish = useCallback(
    (commit: boolean) => {
      const g = gesture.current;
      gesture.current = null;
      stopScrolling();
      setSweep(null);
      if (!g || !commit) return;
      // A tap, or a twitch of a few pixels, named an hour but no length: the
      // span itself is under the schema's floor and nothing the route would
      // accept, so the default length stands in for it rather than the gesture
      // cancelling. A press on the rail is the planner saying "a window here",
      // which is the same thing a click on the empty day says about an item —
      // see `minMinutes` and `defaultMinutes` for why those are two numbers.
      //
      // The offset is the span's own either way. It is `from` for a click, and
      // for a sub-floor drag it is still the earlier of the two edges the
      // planner touched.
      const { minMinutes, defaultMinutes, onSweep } = hostRef.current;
      onSweep(
        g.span.durationMinutes < minMinutes
          ? { offsetMinutes: g.span.offsetMinutes, durationMinutes: defaultMinutes }
          : g.span,
      );
    },
    [stopScrolling],
  );

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || gesture.current) return;

    const target = event.currentTarget;
    const scroller = target.closest('[data-timeline-scroll]') as HTMLElement | null;
    const { scale, totalHeight, viewport } = hostRef.current;
    const window = scroller ? viewport(scroller) : { top: 0, height: 0 };

    const g: SweepGesture = {
      pointerId: event.pointerId ?? 0,
      scroller,
      window,
      target,
      captured: false,
      scale,
      totalHeight,
      from: 0,
      clientY: event.clientY,
      autoScroll: 0,
      span: { offsetMinutes: 0, durationMinutes: 0 },
    };
    g.from = offsetAt(g, event.clientY);
    g.span = { offsetMinutes: g.from, durationMinutes: 0 };
    gesture.current = g;
    // On pointerdown, unlike a block drag: the rail is a strip a few pixels
    // wide, so the pointer is outside it before the first move lands, and
    // there is no field underneath for a retargeted click to be stolen from.
    takeCapture(g);
    // Drawn from the first pixel, so the rail answers the press rather than
    // staying blank until the pointer has travelled far enough to matter.
    setSweep(g.span);
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g) return;
      g.clientY = event.clientY;
      apply(g);
      pump(g.autoScroll !== 0);
    },
    [apply, pump],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g) return;
      g.clientY = event.clientY;
      apply(g);
      finish(true);
    },
    [apply, finish],
  );

  const onPointerCancel = useCallback(() => finish(false), [finish]);

  return { sweep, onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}

/**
 * One snap step on an arrow key — the accessible twin of the drag, not a
 * simulation of one.
 */
export function nudgedOffset(offsetMinutes: number, steps: number, fine: boolean): number {
  const step = fine ? 1 : SNAP_MINUTES;
  return snapMinutes(offsetMinutes + steps * step, { fine });
}
